import React, { useState } from "react";
import { Button, Icon, Input, Table, Message } from "semantic-ui-react";
import { map } from "lodash";
import { toast } from "react-toastify";
import { ModalConfirm } from "../../../Common";
import { useAuth } from "../../../../hooks";
import { addAreaApi, updateAreaApi, deleteAreaApi } from "../../../../api/area";
import "./AreasManager.scss";

// Configuración → Mesas → Ambientes: los espacios del local donde están las
// mesas del salón (Salón, Piso 2, Terraza, Jardín…). Nombre libre y orden; el
// orden es el de las pestañas en Pedidos. Un ambiente con mesas no se borra.
// Con un solo ambiente su nombre no se muestra en ninguna pantalla.
export function AreasManager(props) {
  const { areas, onChange } = props;
  const { auth } = useAuth();
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null); // {id, name}
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (action, message) => {
    setBusy(true);
    try {
      await action();
      if (message) toast.success(message);
      onChange();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const onAdd = () =>
    run(async () => {
      const order = areas.length ? Math.max(...areas.map((a) => a.order)) + 1 : 0;
      await addAreaApi({ name: newName.trim(), order }, auth.token);
      setNewName("");
    }, "Ambiente creado");

  const onRename = () =>
    run(async () => {
      await updateAreaApi(editing.id, { name: editing.name.trim() }, auth.token);
      setEditing(null);
    }, "Ambiente renombrado");

  // Subir o bajar: intercambia el orden con el vecino (orden de las pestañas).
  const move = (index, delta) =>
    run(async () => {
      const list = [...areas];
      const other = index + delta;
      [list[index], list[other]] = [list[other], list[index]];
      await Promise.all(
        list.map((area, i) =>
          area.order === i ? null : updateAreaApi(area.id, { order: i }, auth.token)
        )
      );
    });

  const onDelete = () =>
    run(async () => {
      await deleteAreaApi(deleteTarget.id, auth.token);
      setDeleteTarget(null);
    }, "Ambiente eliminado");

  return (
    <div className="areas-manager">
      <h3>Ambientes</h3>
      <p className="areas-manager__hint">
        Los espacios del local: Salón, Terraza, Piso 2, Jardín… Cada ambiente numera sus
        mesas desde 1. Con un solo ambiente, su nombre no aparece en las pantallas.
      </p>

      <Table compact unstackable className="areas-manager__table">
        <Table.Body>
          {map(areas, (area, index) => (
            <Table.Row key={area.id}>
              <Table.Cell>
                {editing?.id === area.id ? (
                  <Input
                    size="small"
                    autoFocus
                    maxLength={40}
                    value={editing.name}
                    onChange={(_, d) => setEditing({ ...editing, name: d.value })}
                    onKeyDown={(e) => e.key === "Enter" && editing.name.trim() && onRename()}
                  />
                ) : (
                  <strong>{area.name}</strong>
                )}
              </Table.Cell>
              <Table.Cell className="areas-manager__count">
                {area.table_count} {area.table_count === 1 ? "mesa" : "mesas"}
              </Table.Cell>
              <Table.Cell textAlign="right" className="areas-manager__actions">
                {editing?.id === area.id ? (
                  <>
                    <Button size="mini" onClick={() => setEditing(null)}>
                      Cancelar
                    </Button>
                    <Button
                      size="mini"
                      primary
                      loading={busy}
                      disabled={!editing.name.trim()}
                      onClick={onRename}
                    >
                      Guardar
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      icon
                      size="mini"
                      basic
                      disabled={index === 0 || busy}
                      onClick={() => move(index, -1)}
                      title="Subir"
                      aria-label="Subir"
                    >
                      <Icon name="arrow up" />
                    </Button>
                    <Button
                      icon
                      size="mini"
                      basic
                      disabled={index === areas.length - 1 || busy}
                      onClick={() => move(index, 1)}
                      title="Bajar"
                      aria-label="Bajar"
                    >
                      <Icon name="arrow down" />
                    </Button>
                    <Button
                      icon
                      size="mini"
                      onClick={() => setEditing({ id: area.id, name: area.name })}
                      title="Renombrar"
                      aria-label="Renombrar"
                    >
                      <Icon name="pencil" />
                    </Button>
                    <Button
                      icon
                      size="mini"
                      negative
                      disabled={area.table_count > 0}
                      onClick={() => setDeleteTarget(area)}
                      title={area.table_count > 0 ? "Tiene mesas: muévelas primero" : "Eliminar"}
                      aria-label="Eliminar"
                    >
                      <Icon name="close" />
                    </Button>
                  </>
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {areas.length === 0 && (
        <Message info size="small">
          Todavía no hay ambientes: al crear la primera mesa del salón se crea «Salón».
        </Message>
      )}

      <label className="areas-manager__add-label" htmlFor="new-area">
        Agregar un ambiente
      </label>
      <div className="areas-manager__add">
        <Input
          id="new-area"
          placeholder="Nuevo ambiente, ej. Terraza"
          maxLength={40}
          value={newName}
          onChange={(_, d) => setNewName(d.value)}
          onKeyDown={(e) => e.key === "Enter" && newName.trim() && onAdd()}
        />
        <Button color="green" loading={busy} disabled={!newName.trim()} onClick={onAdd}>
          <Icon name="plus" /> Agregar
        </Button>
      </div>

      <ModalConfirm
        title="¿Eliminar ambiente?"
        content={deleteTarget ? `Se eliminará «${deleteTarget.name}». No tiene mesas.` : ""}
        show={!!deleteTarget}
        onCloseText="Cancelar"
        onClose={() => setDeleteTarget(null)}
        onConfirmText="Eliminar"
        onConfirm={onDelete}
      />
    </div>
  );
}
